// `visitResults`: Swift `VisitResult` (GroundTeam/Models/Pipeline.swift): the configurable outcomes of a visit ("Not
// Home", "Appointment Set", "Sold" …) that color pins. Decoding template `VisitResult(name: "")`
// (GroundTeam/Sync/ResilientDecoding.swift:119).

import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  BOOL,
  INT,
  STRING,
  UUID,
  createModel,
  decodeModel,
  field,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface VisitResultPayload extends Passthrough {
  id: string;
  name: string;
  colorHex: string;
  /** Int. */
  order: number;
  /** Counts as a conversation for contact-rate metrics. */
  countsAsContact: boolean;
  isPositive: boolean;
  /** Logging this result moves the record to this stage. */
  movesToStageId?: string;
}

export const VISIT_RESULT_KEYS = [
  'id',
  'name',
  'colorHex',
  'order',
  'countsAsContact',
  'isPositive',
  'movesToStageId',
] as const satisfies readonly (keyof Known<VisitResultPayload>)[];

export const DEFAULT_VISIT_RESULT_COLOR = '#8C919E';

export const VISIT_RESULT_FIELDS: FieldMap<VisitResultPayload> = {
  id: field.strict(UUID),
  name: field.req(STRING, () => ''),
  colorHex: field.req(STRING, () => DEFAULT_VISIT_RESULT_COLOR),
  order: field.req(INT, () => 0),
  countsAsContact: field.req(BOOL, () => true),
  isPositive: field.req(BOOL, () => false),
  movesToStageId: field.opt(UUID),
};

export function decodeVisitResult(raw: JsonObject, envelopeId?: string): Decoded<VisitResultPayload> {
  return decodeModel(VISIT_RESULT_FIELDS, raw, {}, envelopeId);
}

export type VisitResultInit = Partial<Known<VisitResultPayload>> & { name: string };

export function newVisitResult(init: VisitResultInit): VisitResultPayload {
  const defaults = {
    id: newId(),
    colorHex: DEFAULT_VISIT_RESULT_COLOR,
    order: 0,
    countsAsContact: true,
    isPositive: false,
  };
  return createModel(VISIT_RESULT_FIELDS, defaults, init, 'visitResults');
}
